// Atmosphere art for Discover suggestion bento cards.
// Soft pastel heroes (Higgsfield) + CSS gradient fallbacks so a missing asset never blanks a card.

import natureHills from '../assets/discover/disc-nature-hills.webp';
import auraOrb from '../assets/discover/disc-aura-orb.webp';
import dreamMist from '../assets/discover/disc-dream-mist.webp';
import selfSilhouette from '../assets/discover/disc-self-silhouette.webp';
import softWash from '../assets/discover/disc-soft-wash.webp';
import flowerField from '../assets/discover/disc-flower-field.webp';
import pastelHouse from '../assets/discover/disc-pastel-house.webp';
import softHorizon from '../assets/discover/disc-soft-horizon.webp';

export type DiscoverArtKey =
  | 'nature'
  | 'aura'
  | 'dream'
  | 'self'
  | 'wash'
  | 'flowers'
  | 'house'
  | 'horizon'
  | 'mint'
  | 'blush'
  | 'lilac';

const IMAGES: Partial<Record<DiscoverArtKey, string>> = {
  nature: natureHills,
  aura: auraOrb,
  dream: dreamMist,
  self: selfSilhouette,
  wash: softWash,
  flowers: flowerField,
  house: pastelHouse,
  horizon: softHorizon,
};

/** Soft pastel CSS atmospheres when we want pattern/gradient instead of a photo. */
const GRADIENTS: Partial<Record<DiscoverArtKey, string>> = {
  mint:
    'radial-gradient(120% 100% at 88% 12%, oklch(0.92 0.06 160), transparent 55%), radial-gradient(100% 120% at 12% 88%, oklch(0.9 0.05 200), transparent 60%), oklch(0.96 0.02 150)',
  blush:
    'radial-gradient(110% 100% at 80% 10%, oklch(0.9 0.07 20), transparent 55%), radial-gradient(100% 110% at 15% 90%, oklch(0.92 0.05 350), transparent 58%), oklch(0.97 0.02 40)',
  lilac:
    'radial-gradient(115% 105% at 85% 8%, oklch(0.9 0.07 300), transparent 54%), radial-gradient(100% 115% at 10% 92%, oklch(0.91 0.05 250), transparent 58%), oklch(0.97 0.02 290)',
};

/** Map template / contextual ids → art key. */
export function artForSuggestion(id: string, category: string): DiscoverArtKey {
  if (id.startsWith('pattern:')) return 'horizon';
  if (id.startsWith('dream:')) {
    if (/confrontation|conflict|argument|fight|chase/i.test(id)) return 'dream';
    return 'dream';
  }
  switch (id) {
    case 'gratitude_3':
    case 'one_kindness':
      return 'flowers';
    case 'body_check':
    case 'release_list':
      return 'nature';
    case 'manifest_desire':
    case 'future_letter':
    case 'alignment_scan':
      return 'aura';
    case 'dream_aftermath':
    case 'dream_symbol':
      return 'dream';
    case 'shadow_avoid':
    case 'pattern_thread':
      return 'self';
    case 'freewrite_5':
      return 'wash';
    case 'hard_conversation':
      return 'dream';
    case 'decision_desk':
      return 'house';
    default:
      break;
  }
  switch (category) {
    case 'practice':
      return 'mint';
    case 'desire':
      return 'aura';
    case 'inner':
      return 'self';
    case 'dream':
      return 'dream';
    case 'life':
      return 'house';
    default:
      return 'lilac';
  }
}

export function DiscoverCardArt({ art }: { art: DiscoverArtKey }) {
  const image = IMAGES[art];
  const gradient = GRADIENTS[art];

  return (
    <div
      className={`ocard-art art-discover art-${art}`}
      aria-hidden="true"
      style={
        image
          ? { backgroundImage: `url(${image})` }
          : gradient
            ? { backgroundImage: gradient, opacity: 0.88 }
            : undefined
      }
    />
  );
}

/** Which cards in a list should span two columns (hero). */
export function isHeroSuggestion(id: string, index: number, total = 4): boolean {
  if (id.startsWith('pattern:') || id.startsWith('dream:')) return true;
  if (index === 0) return true;
  // Close the grid with a second wide card when we have an odd leftover.
  if (total >= 4 && index === total - 1 && total % 2 === 0) return false;
  return false;
}
